import React, { useEffect, useMemo, useRef, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { useParams } from "react-router-dom";
import toast from "react-hot-toast";
import { Music, X, ChevronDown, AlertTriangle, AlertCircle } from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import { clientSubmitOrderDetails, getClientOrderById } from "../../redux/reduxSlices/ordersSlice";

const ClientJingleDetailsForm = () => {
    const { id } = useParams();
    const dispatch = useDispatch();
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const dropdownRef = useRef(null);
    const [isLoading, setIsLoading] = useState(false);
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [formDataToSubmit, setFormDataToSubmit] = useState(null);

    const { clientOrderByIdData, loading } = useSelector((state) => state.orders);

    const [submitting, setSubmitting] = useState(false);
    const [locked, setLocked] = useState(false);
    const [orderBasics, setOrderBasics] = useState(null);
    const [orderNotFound, setOrderNotFound] = useState(false);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsDropdownOpen(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    const industryCategories = useMemo(
        () => [
            "Food & Beverage",
            "Technology",
            "Fashion",
            "Healthcare",
            "Education",
            "Real Estate",
            "Automotive",
            "Entertainment",
            "Retail",
            "Finance",
            "Travel & Tourism",
            "Others",
        ],
        []
    );

    const toneOptions = useMemo(
        () => [
            "Fast-paced and energetic",
            "Slow and calming",
        ],
        []
    );

    const voiceOptions = useMemo(
        () => ["Male Voice", "Female Voice",],
        []
    );

    const targetAudienceOptions = ["Children", "Teenagers", "Adults", "Seniors"];

    const {
        register,
        handleSubmit,
        control,
        reset,
        formState: { errors },
    } = useForm({
        defaultValues: {
            companyName: "",
            gstNumber: "",
            companyAddress: "",
            companyCity: "",
            brandName: "",
            website: "",
            instagramLink: "",
            companyDetails: "",
            preferredLanguage: "",
            industryCategory: "",
            targetAudience: [],
            tone: "",
            voicePreference: "",
            tagline: "",
            keywords: "",
            restrictions: "",
            jingleRequirements: "",
        },
    });

    // Fetch order
    useEffect(() => {
        if (id) {
            dispatch(getClientOrderById(id));
        }
    }, [id, dispatch]);

    // Handle API response and prefill form
    useEffect(() => {
        if (!clientOrderByIdData) return;

        // Check if order was not found
        if (clientOrderByIdData.success === false) {
            setOrderNotFound(true);
            return;
        }

        // Reset not found state if order exists
        setOrderNotFound(false);

        const order = clientOrderByIdData.order;
        if (!order || !id) return;

        // lock condition (server should enforce also)
        const alreadySubmitted = !!order?.client_form_submitted;
        setLocked(alreadySubmitted);

        const targetAudienceArr = order.target_audience || [];

        // Basics
        setOrderBasics({
            name: order?.name || "",
            email: order?.email || "",
            mobile: order?.mobile_number || "",
            order_id: order?.order_id || id,
            song_duration: order?.song_duration || "",
        });

        // keywords can be array or string; normalize to string for textarea
        const keywordsText = Array.isArray(order?.keywords_to_highlight)
            ? order.keywords_to_highlight.join(", ")
            : typeof order?.keywords_to_highlight === "string"
                ? order.keywords_to_highlight
                : "";

        reset({
            companyName: order?.company_name || "",
            gstNumber: order?.gst_number || "",
            companyAddress: order?.company_address || "",
            companyCity: order?.company_city || "",
            brandName: order?.brand_name_in_jingle || "",
            website: order?.website || "",
            instagramLink: order?.instagram_link || "",
            companyDetails: order?.company_details || "",
            preferredLanguage: order?.preferred_language || "",
            industryCategory: order?.brand_category || "",
            targetAudience: targetAudienceArr,
            tone: order?.tone || "",
            voicePreference: order?.voice_preference || "",
            tagline: order?.tagline_or_slogan || "",
            keywords: keywordsText,
            restrictions: order?.restrictions || "",
            jingleRequirements: order?.jingle_requirements || "",
        });
    }, [clientOrderByIdData, id, reset]);

    const preparePayload = (data) => {
        return {
            // optional
            company_name: data.companyName?.trim() || "",
            gst_number: data.gstNumber?.trim() || "",
            company_address: data.companyAddress?.trim() || "",
            website: data.website?.trim() || "",
            instagram_link: data.instagramLink?.trim() || "",
            timeline: data.timeline?.trim() || "",
            jingle_requirements: data.jingleRequirements?.trim() || "",

            // ✅ required (match backend keys exactly)
            company_city: data.companyCity?.trim(),
            brand_name_in_jingle: data.brandName?.trim(),
            company_details: data.companyDetails?.trim(),
            preferred_language: data.preferredLanguage?.trim(),
            brand_category: data.industryCategory?.trim(),

            // arrays
            target_audience: Array.isArray(data.targetAudience)
                ? data.targetAudience
                : (data.targetAudience || "")
                    .split(",")
                    .map((v) => v.trim())
                    .filter(Boolean),

            tone: data.tone?.trim(),
            voice_preference: data.voicePreference?.trim(),
            tagline_or_slogan: data.tagline?.trim(),

            keywords_to_highlight: (data.keywords || "")
                .split(",")
                .map((k) => k.trim())
                .filter(Boolean),

            restrictions: data.restrictions?.trim(),
        };
    };

    const handleFormSubmit = async (data) => {
        if (locked) return;

        const payload = preparePayload(data);
        setFormDataToSubmit({ id, payload });
        setShowConfirmModal(true);
    };

    const confirmSubmit = async () => {
        if (!formDataToSubmit) return;

        try {
            setSubmitting(true);
            const res = await dispatch(clientSubmitOrderDetails(formDataToSubmit)).unwrap();

            if (res?.success) {
                toast.success("Submitted successfully. This form is now locked.");
                setLocked(true);
                setShowConfirmModal(false);
            } else {
                toast.error(res?.detail || "Failed to submit");
            }
        } catch (e) {
            console.log("error", e);
            toast.error(e?.message || "Something went wrong");
        } finally {
            setSubmitting(false);
            setFormDataToSubmit(null);
        }
    };

    const cancelSubmit = () => {
        setShowConfirmModal(false);
        setFormDataToSubmit(null);
    };

    // Show loading state
    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600 mx-auto"></div>
                    <p className="mt-4 text-gray-600">Loading order details...</p>
                </div>
            </div>
        );
    }

    // Show not found state
    if (orderNotFound) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
                <div className="max-w-md w-full bg-white rounded-lg border border-gray-200 overflow-hidden">
                    <div className="p-6">
                        <div className="flex items-center justify-center mb-4">
                            <AlertCircle className="w-12 h-12 text-red-500" />
                        </div>
                        <h2 className="text-xl font-bold text-gray-900 text-center mb-2">
                            Order Not Found
                        </h2>
                        <p className="text-gray-600 text-center mb-6">
                            {clientOrderByIdData?.message || "The order you are looking for does not exist or you don't have permission to view it."}
                        </p>
                        <div className="bg-red-50 border border-red-200 rounded p-3 mb-4">
                            <p className="text-sm text-red-800 text-center">
                                Order ID: <span className="font-mono font-medium">{id}</span>
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 p-3 md:p-6">
            {/* Confirmation Modal */}
            {showConfirmModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg max-w-md w-full p-6 shadow-xl">
                        <div className="flex items-start gap-3 mb-4">
                            <div className="flex-shrink-0">
                                <AlertTriangle className="w-6 h-6 text-orange-600" />
                            </div>
                            <div>
                                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                                    Confirm Submission
                                </h3>
                                <p className="text-sm text-gray-600">
                                    Are you sure you want to submit the form? Once submitted, you cannot make any changes. Please review all information carefully.
                                </p>
                            </div>
                        </div>

                        <div className="bg-yellow-50 border border-yellow-200 rounded p-3 mb-4">
                            <p className="text-xs text-yellow-800 font-medium">
                                ⚠️ This action is irreversible. The form will be locked for editing.
                            </p>
                        </div>

                        <div className="flex justify-end gap-3">
                            <button
                                type="button"
                                onClick={cancelSubmit}
                                disabled={submitting}
                                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded disabled:opacity-50"
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={confirmSubmit}
                                disabled={submitting}
                                className="px-4 py-2 text-sm font-medium text-white bg-gradient-to-br from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 rounded disabled:opacity-50 flex items-center gap-2"
                            >
                                {submitting ? (
                                    <>
                                        <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></span>
                                        Submitting...
                                    </>
                                ) : (
                                    "Yes, Submit Form"
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <div className="max-w-4xl mx-auto bg-white rounded-lg border border-gray-200 overflow-hidden">
                <div className="p-4 border-b border-gray-200 flex items-center justify-between">
                    <h1 className="text-base font-bold text-gray-900 flex items-center gap-2">
                        <Music className="w-4 h-4 text-orange-600" />
                        Jingle Details (Order #{orderBasics?.order_id || id})
                    </h1>


                    {locked ? (
                        <span className="text-xs px-2 py-1 rounded bg-green-100 text-green-700 border border-green-200">
                            Submitted
                        </span>
                    ) : (
                        <span className="text-xs px-2 py-1 rounded bg-orange-50 text-orange-700 border border-orange-200">
                            Please fill and submit once
                        </span>
                    )}
                </div>
                <div className="p-2 border-b border-gray-200 flex flex-col items-start justify-between">
                    <h3 className="text-base font-bold text-gray-900 flex items-center gap-2">
                        Instructions
                    </h3>
                    <ul className="list-disc pl-6 space-y-2 text-sm">
                        <li>Once you fill out the form, we will share the lyrics for your approval.</li>
                        <li>After the lyrics are approved, we will create and share three versions of the jingle based on the same lyrics.</li>
                        {/* <li>The duration of each version will range between 20 to 40 seconds </li> */}
                    </ul>
                </div>

                {/* Basic Info */}
                <div className="p-4 bg-gray-50 border-b border-gray-200">
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-2 text-sm">
                        <div>
                            <div className="text-xs text-gray-500">Name</div>
                            <div className="font-medium text-gray-900">{orderBasics?.name || "-"}</div>
                        </div>
                        <div>
                            <div className="text-xs text-gray-500">Email</div>
                            <div className="font-medium text-gray-900">{orderBasics?.email || "-"}</div>
                        </div>
                        <div>
                            <div className="text-xs text-gray-500">Mobile</div>
                            <div className="font-medium text-gray-900">{orderBasics?.mobile || "-"}</div>
                        </div>
                        <div>
                            <div className="text-xs text-gray-500">Song Duration</div>
                            <div className="font-medium text-gray-900">{orderBasics?.song_duration || "-"}</div>
                        </div>
                    </div>
                </div>

                {locked && (
                    <div className="p-4">
                        <div className="rounded border border-green-200 bg-green-50 p-3 text-sm text-green-800">
                            You've successfully submitted this form. If you have any changes please contact sales co-ordinator.
                        </div>
                    </div>
                )}

                <form onSubmit={handleSubmit(handleFormSubmit)} className="p-4">
                    <div className="space-y-6">
                        <section>
                            <h3 className="text-sm font-semibold text-gray-900 mb-3 pb-1 border-b">
                                Company & Brand
                            </h3>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                <div className="md:col-span-2">
                                    <label className="block text-xs font-medium text-gray-700 mb-1.5">
                                        Company Name
                                    </label>
                                    <input
                                        type="text"
                                        {...register("companyName")}
                                        disabled={locked || loading}
                                        className="w-full px-2.5 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-orange-500 disabled:bg-gray-100"
                                    />
                                </div>

                                <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1.5">
                                        GST Number
                                    </label>
                                    <input
                                        type="text"
                                        {...register("gstNumber")}
                                        disabled={locked || loading}
                                        className="w-full px-2.5 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-orange-500 disabled:bg-gray-100"
                                    />
                                </div>

                                <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1.5">
                                        Company City <span className="text-red-600">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        {...register("companyCity", { required: "Company city is required" })}
                                        disabled={locked || loading}
                                        className={`w-full px-2.5 py-1.5 border rounded text-sm focus:outline-none focus:ring-1 focus:ring-orange-500 disabled:bg-gray-100 ${errors.companyCity ? "border-red-300 bg-red-50" : "border-gray-300"
                                            }`}
                                    />
                                    {errors.companyCity && (
                                        <p className="text-red-600 text-xs mt-1">{errors.companyCity.message}</p>
                                    )}
                                </div>

                                <div className="md:col-span-2">
                                    <label className="block text-xs font-medium text-gray-700 mb-1.5">
                                        Company Address
                                    </label>
                                    <textarea
                                        {...register("companyAddress")}
                                        rows={2}
                                        disabled={locked || loading}
                                        className="w-full px-2.5 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-orange-500 disabled:bg-gray-100"
                                    />
                                </div>

                                <div className="md:col-span-2">
                                    <label className="block text-xs font-medium text-gray-700 mb-1.5">
                                        Brand Name for Jingle <span className="text-red-600">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        {...register("brandName", { required: "Brand name is required" })}
                                        disabled={locked || loading}
                                        className={`w-full px-2.5 py-1.5 border rounded text-sm focus:outline-none focus:ring-1 focus:ring-orange-500 disabled:bg-gray-100 ${errors.brandName ? "border-red-300 bg-red-50" : "border-gray-300"
                                            }`}
                                    />
                                    {errors.brandName && (
                                        <p className="text-red-600 text-xs mt-1">{errors.brandName.message}</p>
                                    )}
                                </div>

                                <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1.5">
                                        Website
                                    </label>
                                    <input
                                        type="url"
                                        {...register("website")}
                                        disabled={locked || loading}
                                        className="w-full px-2.5 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-orange-500 disabled:bg-gray-100"
                                    />
                                </div>

                                <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1.5">
                                        Instagram Link
                                    </label>
                                    <input
                                        type="url"
                                        {...register("instagramLink")}
                                        disabled={locked || loading}
                                        className="w-full px-2.5 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-orange-500 disabled:bg-gray-100"
                                    />
                                </div>

                                <div className="md:col-span-2">
                                    <label className="block text-xs font-medium text-gray-700 mb-1.5">
                                        Company Details <span className="text-red-600">*</span>
                                    </label>
                                    <textarea
                                        {...register("companyDetails", { required: "Company details are required" })}
                                        rows={2}
                                        disabled={locked || loading}
                                        className={`w-full px-2.5 py-1.5 border rounded text-sm focus:outline-none focus:ring-1 focus:ring-orange-500 disabled:bg-gray-100 ${errors.companyDetails ? "border-red-300 bg-red-50" : "border-gray-300"
                                            }`}
                                    />
                                    {errors.companyDetails && (
                                        <p className="text-red-600 text-xs mt-1">{errors.companyDetails.message}</p>
                                    )}
                                </div>
                            </div>
                        </section>

                        <section>
                            <h3 className="text-sm font-semibold text-gray-900 mb-3 pb-1 border-b">
                                Jingle Requirements
                            </h3>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1.5">
                                        Preferred Language <span className="text-red-600">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        {...register("preferredLanguage", { required: "Preferred language is required" })}
                                        disabled={locked || loading}
                                        className={`w-full px-2.5 py-1.5 border rounded text-sm focus:outline-none focus:ring-1 focus:ring-orange-500 disabled:bg-gray-100 ${errors.preferredLanguage ? "border-red-300 bg-red-50" : "border-gray-300"
                                            }`}
                                    />
                                    {errors.preferredLanguage && (
                                        <p className="text-red-600 text-xs mt-1">{errors.preferredLanguage.message}</p>
                                    )}
                                </div>

                                <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1.5">
                                        Industry Category <span className="text-red-600">*</span>
                                    </label>
                                    <Controller
                                        name="industryCategory"
                                        control={control}
                                        rules={{ required: "Industry category is required" }}
                                        render={({ field }) => (
                                            <>
                                                <select
                                                    {...field}
                                                    disabled={locked || loading}
                                                    className={`w-full px-2.5 py-1.5 border rounded text-sm focus:outline-none focus:ring-1 focus:ring-orange-500 disabled:bg-gray-100 ${errors.industryCategory ? "border-red-300 bg-red-50" : "border-gray-300"
                                                        }`}
                                                >
                                                    <option value="">Select category</option>
                                                    {industryCategories.map((category) => (
                                                        <option key={category} value={category}>
                                                            {category}
                                                        </option>
                                                    ))}
                                                </select>
                                                {errors.industryCategory && (
                                                    <p className="text-red-600 text-xs mt-1">{errors.industryCategory.message}</p>
                                                )}
                                            </>
                                        )}
                                    />
                                </div>

                                <div className="md:col-span-2">
                                    <label className="block text-xs font-medium text-gray-700 mb-1.5">
                                        Target Audience <span className="text-red-600">*</span>
                                    </label>
                                    <Controller
                                        name="targetAudience"
                                        control={control}
                                        rules={{
                                            required: "Please select at least one target audience",
                                            validate: value => value && value.length > 0 || "Please select at least one target audience"
                                        }}
                                        render={({ field }) => (
                                            <div className="relative" ref={dropdownRef}>
                                                <button
                                                    type="button"
                                                    onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                                                    className={`w-full px-2.5 py-1.5 border rounded text-sm text-left focus:outline-none focus:ring-1 focus:ring-orange-500 ${errors.targetAudience ? "border-red-300 bg-red-50" : "border-gray-300"
                                                        } ${isLoading ? "bg-gray-100" : ""}`}
                                                    disabled={isLoading || locked || loading}
                                                >
                                                    <div className="flex justify-between items-center">
                                                        <span className="truncate">
                                                            {field.value?.length > 0
                                                                ? field.value.join(", ")
                                                                : "Select target audience"}
                                                        </span>
                                                        <ChevronDown
                                                            className={`w-4 h-4 transition-transform ${isDropdownOpen ? "rotate-180" : ""
                                                                }`}
                                                        />
                                                    </div>
                                                </button>

                                                {isDropdownOpen && (
                                                    <div className="absolute z-10 mt-1 w-full bg-white border border-gray-300 rounded shadow-lg max-h-60 overflow-y-auto">
                                                        {targetAudienceOptions.map((option) => (
                                                            <label
                                                                key={option}
                                                                className="flex items-center px-3 py-2 hover:bg-gray-50 cursor-pointer"
                                                            >
                                                                <input
                                                                    type="checkbox"
                                                                    checked={field.value?.includes(option)}
                                                                    onChange={(e) => {
                                                                        if (e.target.checked) {
                                                                            field.onChange([...field.value, option]);
                                                                        } else {
                                                                            field.onChange(
                                                                                field.value.filter((value) => value !== option)
                                                                            );
                                                                        }
                                                                    }}
                                                                    className="h-4 w-4 text-orange-600 border-gray-300 rounded focus:ring-orange-500"
                                                                    disabled={isLoading || locked || loading}
                                                                />
                                                                <span className="ml-2 text-sm text-gray-700">{option}</span>
                                                            </label>
                                                        ))}
                                                    </div>
                                                )}
                                                {errors.targetAudience && (
                                                    <p className="text-red-600 text-xs mt-1">
                                                        {errors.targetAudience.message}
                                                    </p>
                                                )}
                                            </div>
                                        )}
                                    />
                                </div>

                                <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1.5">
                                        Tone <span className="text-red-600">*</span>
                                    </label>
                                    <Controller
                                        name="tone"
                                        control={control}
                                        rules={{ required: "Tone is required" }}
                                        render={({ field }) => (
                                            <>
                                                <select
                                                    {...field}
                                                    disabled={locked || loading}
                                                    className={`w-full px-2.5 py-1.5 border rounded text-sm focus:outline-none focus:ring-1 focus:ring-orange-500 disabled:bg-gray-100 ${errors.tone ? "border-red-300 bg-red-50" : "border-gray-300"
                                                        }`}
                                                >
                                                    <option value="">Select tone</option>
                                                    {toneOptions.map((t) => (
                                                        <option key={t} value={t}>
                                                            {t}
                                                        </option>
                                                    ))}
                                                </select>
                                                {errors.tone && (
                                                    <p className="text-red-600 text-xs mt-1">{errors.tone.message}</p>
                                                )}
                                            </>
                                        )}
                                    />
                                </div>

                                <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1.5">
                                        Voice Preference <span className="text-red-600">*</span>
                                    </label>
                                    <Controller
                                        name="voicePreference"
                                        control={control}
                                        rules={{ required: "Voice preference is required" }}
                                        render={({ field }) => (
                                            <>
                                                <select
                                                    {...field}
                                                    disabled={locked || loading}
                                                    className={`w-full px-2.5 py-1.5 border rounded text-sm focus:outline-none focus:ring-1 focus:ring-orange-500 disabled:bg-gray-100 ${errors.voicePreference ? "border-red-300 bg-red-50" : "border-gray-300"
                                                        }`}
                                                >
                                                    <option value="">Select voice</option>
                                                    {voiceOptions.map((v) => (
                                                        <option key={v} value={v}>
                                                            {v}
                                                        </option>
                                                    ))}
                                                </select>
                                                {errors.voicePreference && (
                                                    <p className="text-red-600 text-xs mt-1">{errors.voicePreference.message}</p>
                                                )}
                                            </>
                                        )}
                                    />
                                </div>

                                <div className="md:col-span-2">
                                    <label className="block text-xs font-medium text-gray-700 mb-1.5">
                                        Tagline <span className="text-red-600">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        {...register("tagline", { required: "Tagline is required" })}
                                        disabled={locked || loading}
                                        className={`w-full px-2.5 py-1.5 border rounded text-sm focus:outline-none focus:ring-1 focus:ring-orange-500 disabled:bg-gray-100 ${errors.tagline ? "border-red-300 bg-red-50" : "border-gray-300"
                                            }`}
                                    />
                                    {errors.tagline && (
                                        <p className="text-red-600 text-xs mt-1">{errors.tagline.message}</p>
                                    )}
                                </div>

                                <div className="md:col-span-2">
                                    <label className="block text-xs font-medium text-gray-700 mb-1.5">
                                        Keywords (comma-separated) <span className="text-red-600">*</span>
                                    </label>
                                    <textarea
                                        {...register("keywords", { required: "Keywords are required" })}
                                        rows={2}
                                        disabled={locked || loading}
                                        className={`w-full px-2.5 py-1.5 border rounded text-sm focus:outline-none focus:ring-1 focus:ring-orange-500 disabled:bg-gray-100 ${errors.keywords ? "border-red-300 bg-red-50" : "border-gray-300"
                                            }`}
                                    />
                                    {errors.keywords && (
                                        <p className="text-red-600 text-xs mt-1">{errors.keywords.message}</p>
                                    )}
                                </div>

                                <div className="md:col-span-2">
                                    <label className="block text-xs font-medium text-gray-700 mb-1.5">
                                        Restrictions <span className="text-red-600">*</span>
                                    </label>
                                    <textarea
                                        {...register("restrictions", { required: "Restrictions are required" })}
                                        rows={2}
                                        disabled={locked || loading}
                                        className={`w-full px-2.5 py-1.5 border rounded text-sm focus:outline-none focus:ring-1 focus:ring-orange-500 disabled:bg-gray-100 ${errors.restrictions ? "border-red-300 bg-red-50" : "border-gray-300"
                                            }`}
                                    />
                                    {errors.restrictions && (
                                        <p className="text-red-600 text-xs mt-1">{errors.restrictions.message}</p>
                                    )}
                                </div>
                            </div>
                        </section>
                    </div>

                    <div className="flex items-center justify-end gap-2 mt-6 border-t border-gray-200 pt-4">
                        <button
                            type="submit"
                            disabled={locked || submitting || loading || orderNotFound}
                            className="px-4 py-2 rounded text-white text-sm font-medium bg-gradient-to-br from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 disabled:opacity-60 disabled:cursor-not-allowed"
                        >
                            {submitting ? "Submitting..." : locked ? "Submitted" : "Submit & Lock"}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default ClientJingleDetailsForm;